/**
 * @typedef {import('BMapsModel').Compound} Compound
 * @typedef {import('BMapsModel').MapCase} MapCase
 * @typedef {import('@mui/x-data-grid').GridColumnGroupingModel} GridColumnGroupingModel
 * @typedef {import('@mui/x-data-grid').GridColumnsPanelProps} GridColumnsPanelProps
 * @typedef {import('@mui/x-data-grid').ColumnsPanelPropsOverrides} ColumnsPanelPropsOverrides
 * @typedef {import ('@mui/x-data-grid').GridColDef} GridColDef
 */

import { useSelector } from 'BMapsRedux';
import _ from 'lodash';

import { App } from 'BMapsSrc/BMapsApp';
import { setActiveChart } from 'BMapsRedux/projectState/access';
import { PropApplicability } from 'BMapsModel';
import {
    HoverableCompoundStructureCell,
    HeaderCell, ProteinCell, CompoundNameCell,
} from './TableCells';
import { BMapsTable, getFormattedValue } from './BMapsTable';
import BMapsTooltip from '../BMapsTooltip';
import { ScoringCoralMap } from './graphs/CoralComponents';
import { isKinaseInProjectCases } from '../dialog/FragmentSearch/utils';
import { CustomColumnsPanel } from './CustomColumnsPanel';
import useCompoundUpdateSubscription from '../common/helpers/useCompoundUpdateSubscription';

/**
 * @param {{
*     setColumnVisibilityModel?: (columnVisibilityModel: GridColumnVisibilityModel) => any,
*     columnVisibilityModel?: GridColumnVisibilityModel,
*     itemSelector?: (item: item) => any,
*     disableTable?: boolean,
*     exportFileName?: string,
*     useUniqueCompounds?: boolean,
*     useAdditionalProperties?: boolean,
*     checkboxSelection?: boolean,
*     setCheckedCompounds?: (compound: Compound) => any,
*     useProteinProperties?: boolean,
*}} props
*/
export function CompoundTable({
    useUniqueCompounds, useProteinProperties, useAdditionalProperties,
    columnVisibilityModel, useCharts=false, tableId='compoundTable',
    initialCheckedCompounds=[],
    setCheckedCompounds, ...props
}) {
    const compounds = useCompoundUpdateSubscription({
        useUniqueCompounds,
        additionalEvents: ['redrawSceneRequest'],
    });

    const { targetList, tableState: allTableState } = useSelector((state) => state.projectState);
    const tableState = allTableState[tableId];

    const perCompoundColumnTitles = ['Compound Name', 'Compound Structure', 'Mol. Weight', 'LogP', 'PSA', 'Fraction Sp3', '# Heavy Atoms', 'Charge', 'HB Donors', 'HB Acceptors', '# Rot. Bonds', '# Lipinski Violations'];
    const perPoseColumnTitles = ['Interaction Score', 'Energy Efficiency', 'Van der Waals', 'Electrostatic Energy', 'ddGs', 'Stress'];

    const tableData = createCompoundTableData({
        compounds,
        useUniqueCompounds,
        useAdditionalProperties,
        useProteinProperties,
        perCompoundColumnTitles,
        perPoseColumnTitles,
        targetList,
    });

    const initialRowSelectionModel = tableData.rows.map((row) => {
        const rowStructure = row['Compound Structure'];
        const compoundStructures = initialCheckedCompounds
            .map((compound) => compound.getUnnamedSmiles());
        if (compoundStructures.includes(rowStructure)) {
            return row.id;
        }
        return undefined;
    }).filter((id) => id !== undefined);

    const availableCharts = [{
        id: 'bmapsTable',
        name: 'Table',
        Component: BMapsTable,
        componentProps: {
            rows: tableData.rows,
            columns: tableData.columns,
            columnGroupingModel: tableData.columnGroupingModel,
            rowItems: tableData.rowItems,
            initialRowSelectionModel,
            columnVisibilityModel: columnVisibilityModel
                || getDefaultVisibleColumns(tableData.columns),
            setCheckedRowItems: setCheckedCompounds,
            customPanel: CustomColumnsPanel,
            duplicateColumnKeys: perPoseColumnTitles,
            setChart: useCharts ? (newChart) => setActiveChart(newChart, tableId) : null,
            ...props,
        },
    }];
    if (tableId === 'scoringTable'
        && isKinaseInProjectCases(compounds.map(
            (compound) => compound.caseData?.mapCase?.projectCase
        ))
    ) {
        availableCharts.push({
            id: 'coralMap',
            name: 'Coral Map',
            Component: ScoringCoralMap,
            componentProps: {
                tableData,
                coralMapState: tableState.chartState.coralMap,
                chart: tableState.activeChart,
                setChart: (newChart) => setActiveChart(newChart, tableId),
                tableId,
            },
        });
    }

    const found = availableCharts.find(
        (chart) => chart.id === tableState.activeChart
    );
    const { Component, componentProps } = found || availableCharts[0];

    return (
        <Component
            availableCharts={useCharts && availableCharts}
            {...componentProps}
        />
    );
}

/**
 * @param {Compound} compound
 * @param {MapCase} mapCase
 * @returns
 */
function getBestPose(mapCase, compound, scoreFn=defaultScore) {
    const compoundKey = compound.getUniqueStructId();
    const poses = App.Workspace.getLoadedCompounds().filter((pose) => {
        const poseKey = pose.getUniqueStructId();
        return (poseKey === compoundKey && pose.caseData.mapCase === mapCase);
    });
    poses.sort((a, b) => sortPoses(a, b, scoreFn));
    return poses[0];
}

/**
 * @param {Compound} compound
 * @returns
 */
function defaultScore(compound) {
    return compound?.getEnergyInfo()?.getEnergyScore();
}

/**
 * @param {Compound} cmpdA
 * @param {Compound} cmpdB
 * @param {(Compound) => Number?} [scoreFn]
 * @returns
 */
function sortPoses(cmpdA, cmpdB, scoreFn=defaultScore) {
    const scoreA = scoreFn(cmpdA);
    const scoreB = scoreFn(cmpdB);
    switch (true) {
        case scoreA == null && scoreB == null: return 0;
        case scoreA == null: return 1;
        case scoreB == null: return -1;
        default: return scoreA - scoreB;
    }
}

/**
     * @param {Compound} compound
     * @param {string} property
     * @returns {Number | null}
     */
const getMolPropOrEnergyValue = (compound, property) => {
    if (property === 'Energy Efficiency') return compound.getMolProp('Energy Efficiency');
    if (property === 'Interaction Score') {
        const enInfo = compound.getEnergyInfo();
        // For some reason, getEnergyScore() returns 0 when there is no energy.
        // Until this is fixed, check availablily first.
        return enInfo.energyScoreAvailable() ? enInfo.getEnergyScore() : '';
    }
    return compound.getEnergyInfo()?.getEnergyValueByType(property);
};

/**
 * @param {Compound} compound
 * @param {*} property
 * @param {MapCase} mapCase
 * @returns
 */
export const getPerPoseValue = (compound, property, mapCase) => {
    if (!compound) return '';
    if (compound.caseData.mapCase !== mapCase) return '';

    let propertyName = property;
    if (property === 'Best Pose Name') return compound.displayName;
    if (property === 'Van der Waals') propertyName = 'vdW';
    if (property === 'Hydrogen Bond Energy') propertyName = 'hbonds';
    if (property === 'Electrostatic Energy') propertyName = 'electrostatics';
    if (property === 'Stress') propertyName = 'stress';
    if (property === 'Docking Score') propertyName = 'dockingScore';

    const propertyValue = getMolPropOrEnergyValue(compound, propertyName);
    if (typeof propertyValue !== 'number') return '';
    return propertyValue;
};

const getPerCompoundValue = (compound, property) => {
    const propertyName = property;
    const value = compound?.getMolProp(propertyName);
    if (typeof value !== 'number') return '';
    return value;
};

function filterByPropApplicability(propList, propApplicability) {
    return propList.filter((sp) => {
        if (!propApplicability) return true;
        const myPropApplicability = sp.traverseForMetadataItem('propApplicability');
        return (
            !myPropApplicability && propApplicability === 'perCompound'
        ) || (
            typeof propApplicability === 'string' && propApplicability === myPropApplicability
        ) || (
            Array.isArray(propApplicability) && propApplicability.includes(myPropApplicability)
        );
    });
}

function listPerCompoundProps(compound) {
    const allProps = compound.listProperties({ includeChildren: true });
    return filterByPropApplicability(allProps, PropApplicability.PERCOMPOUND);
}

function listProteinProps(mapCase) {
    return mapCase?.listProperties({ includeChildren: true }) || [];
}

function listPerProteinProps(compound) {
    const { mapCase } = App.getDataParents(compound);
    const proteinProps = listProteinProps(mapCase);
    const allCmpdProps = compound.listProperties({ includeChildren: true });
    const perProteinCmpdProps = filterByPropApplicability(
        allCmpdProps, [PropApplicability.PERPOSE, PropApplicability.PERCOMPOUNDTARGET]
    );
    return [...proteinProps, ...perProteinCmpdProps];
}

function labelOrName(prop) {
    return prop.getMetadata().label || prop.name;
}

function makePropKey(propInfo) {
    const fullPath = propInfo.getScopeListNames({ includeSelf: true, includeRoot: true });
    const key = fullPath.join('__');
    return key;
}

function propTooltip(propInfo) {
    let tooltip = labelOrName(propInfo);
    const description = propInfo.getMetadata().description;
    if (description) tooltip += `: ${description}`;
    const metaDataTooltip = propInfo.getMetadata().tooltip;
    if (metaDataTooltip) tooltip = metaDataTooltip;
    return tooltip;
}

function makePerProtColKey(prop, mapCase, index) {
    if (!mapCase) {
        return prop;
    }
    return `${prop} ${mapCase.displayName} (Protein ${index + 1})`;
}

function makeColPickerTitle(propInfo) {
    const name = labelOrName(propInfo);
    const scopeNameList = propInfo.getScopeListNames({ includeRoot: true });
    if (scopeNameList[scopeNameList.length-1] === 'sdfProps') {
        return `SDF: ${name}`;
    }
    if (scopeNameList[0] === 'target') {
        return `Target ${name}`;
    } else if (scopeNameList[0] === 'compound') {
        const propApplicability = propInfo.traverseForMetadataItem('propApplicability');
        if (
            [PropApplicability.PERPOSE, PropApplicability.PERCOMPOUNDTARGET]
                .includes(propApplicability)
        ) {
            return `Pose ${name}`;
        } else {
            return `Cmpd ${name}`;
        }
    } else {
        return propInfo.name;
    }
}

/**
 * @param {{
 *     compounds: Compound[],
 *     useUniqueCompounds?: boolean,
 *     useAdditionalProperties?: boolean,
 *     useProteinProperties?: boolean,
 * }} tableProperties
 * @returns
 */
function createCompoundTableData({
    compounds,
    perCompoundColumnTitles,
    perPoseColumnTitles,
    useProteinProperties=false,
    useAdditionalProperties=false,
    useUniqueCompounds=false,
    targetList=[],
}) {
    let loadedProteins = [];

    if (useProteinProperties) {
        loadedProteins = targetList.map((target) => target.mapCase);
    }

    if (useUniqueCompounds) {
        perPoseColumnTitles.push('Best Pose Name');
    }

    /** @type {string[]} */
    const defaultPerCompoundCols = perCompoundColumnTitles.map(
        (title) => ({ key: title, title, colPickerTitle: title })
    );
    const defaultPerPoseCols = perPoseColumnTitles.map(
        (title) => ({ key: title, title, colPickerTitle: title })
    );
    let otherCmpdPropCols = [];
    let otherProteinPropCols = [];
    if (useAdditionalProperties) {
        otherCmpdPropCols = _.uniqBy(
            compounds.flatMap(listPerCompoundProps).map((propInfo) => ({
                key: makePropKey(propInfo),
                title: labelOrName(propInfo),
                colPickerTitle: makeColPickerTitle(propInfo),
                tooltip: propTooltip(propInfo),
            })),
            (entry) => entry.key
        );
        otherProteinPropCols = _.uniqBy(
            compounds.flatMap(listPerProteinProps).map((propInfo) => ({
                key: makePropKey(propInfo),
                title: labelOrName(propInfo),
                colPickerTitle: makeColPickerTitle(propInfo),
                tooltip: propTooltip(propInfo),
            })),
            (entry) => entry.key
        );
    }

    const allPerCompoundCols = [...defaultPerCompoundCols, ...otherCmpdPropCols];
    const allPerPoseCols = [...defaultPerPoseCols, ...otherProteinPropCols];

    /**
     * @type {GridColumnGroupingModel}
     */
    let columnGroupingModel;
    if (useProteinProperties) {
        columnGroupingModel = [
            {
                groupId: 'perCompound',
                headerClassName: 'none-header',
                headerName: ' ',
                children: allPerCompoundCols.map(({ key }) => ({ field: key })),
            },
            ...loadedProteins.map((mapCase, index) => ({
                groupId: `${mapCase.displayName} (Protein ${index + 1})`,
                headerName: mapCase.getLongName(),
                headerAlign: 'center',
                headerClassName: 'protein-header',
                children: allPerPoseCols.map(
                    ({ key }) => ({ field: makePerProtColKey(key, mapCase, index) })
                ),
            })),
        ];
    }

    /**
     * @type {GridColDef[]}
     * Definitions:
     * - field is the programmatic key
     * - headerName is what shows up in the CSV
     * - title is what shows up in the column picker widget
     * - renderHeader() is what shows up in the table header
     */
    const columns = [
        ...allPerCompoundCols.map(({
            key, title, colPickerTitle, tooltip,
        }) => {
            /**
             * @type {GridColDef}
             */
            const columnInfo = {
                field: key,
                headerName: title,
                title: colPickerTitle,
                description: tooltip || title,
                type: 'number',
                flex: 1,
                minWidth: 80,
                renderCell: ({ value }) => (
                    <BMapsTooltip title={value} placement="bottom-start">
                        <span>{getFormattedValue(value)}</span>
                    </BMapsTooltip>
                ),
                renderHeader: HeaderCell,
            };

            if (title === 'Compound Structure') {
                columnInfo.renderCell = ({ value, row }) => (
                    <HoverableCompoundStructureCell smiles={value} uniqueId={row.id} compoundName={row['Compound Name']} />
                );
                columnInfo.sortable = false;
                columnInfo.filterable = false;
                columnInfo.type = 'string';
                columnInfo.headerName = 'Structure';
            }

            if (title === 'Compound Name') {
                columnInfo.type = 'string';
                columnInfo.headerName = 'Compound';
                columnInfo.renderCell = ({ value, row }) => (
                    <CompoundNameCell row={row} value={value} />
                );
            }

            return columnInfo;
        }),
        ...loadedProteins.flatMap((mapCase, index) => (
            allPerPoseCols.map(({
                key, title, colPickerTitle, tooltip,
            }) => ({
                field: makePerProtColKey(key, mapCase, index),
                headerName: mapCase ? `${title} (${mapCase.displayName})` : title,
                description: tooltip || title,
                title: colPickerTitle,
                type: 'number',
                flex: 1,
                minWidth: 80,
                renderHeader: (props) => <HeaderCell {...props} content={title} />,
                renderCell: ({ value, row }) => (
                    <ProteinCell
                        value={value}
                        mapCase={mapCase}
                        row={row}
                        index={index}
                        useUniqueCompounds={useUniqueCompounds}
                    />
                ),
            }))
        )),
    ];

    const rows = compounds.map((compound, id) => {
        /**
         * @type {GridValidRowModel}
         */
        const properties = { id };
        defaultPerCompoundCols.forEach(({ key, title }) => {
            if (title === 'Compound Structure') {
                properties[key] = compound.getUnnamedSmiles();
            } else if (title === 'Compound Name') {
                properties[key] = compound.displayName;
            } else {
                properties[key] = getPerCompoundValue(compound, key);
            }
        });
        const otherProps = listPerCompoundProps(compound);
        if (otherProps) {
            for (const propInfo of otherProps) {
                properties[makePropKey(propInfo)] = propInfo.value;
            }
        }
        properties.resSpec = compound.resSpec;
        for (const [index, mapCase] of loadedProteins.entries()) {
            const relevantCompound = useUniqueCompounds ? getBestPose(mapCase, compound) : compound;
            if (!relevantCompound) {
                continue;
            }
            for (const { key } of defaultPerPoseCols) {
                const colKey = makePerProtColKey(key, mapCase, index);
                properties[colKey] = getPerPoseValue(relevantCompound, key, mapCase);
            }
            for (const propInfo of listPerProteinProps(relevantCompound)) {
                const { value } = propInfo;
                const colKey = makePerProtColKey(makePropKey(propInfo), mapCase, index);
                properties[colKey] = relevantCompound.caseData.mapCase === mapCase ? value : '';
            }
        }
        return properties;
    });

    return {
        columnGroupingModel,
        columns,
        rows,
        rowItems: compounds,
        allPerPoseCols,
        allPerCompoundCols,
    };
}

function getDefaultVisibleColumns(columns) {
    const visibilityModel = {
        'Compound Name': true,
        'Compound Structure': true,
    };

    columns.forEach((column) => {
        if (column.field.includes('Interaction Score')) visibilityModel[column.field] = true;
    });

    return (visibilityModel);
}
